import {Button, Modal} from "antd";
import {FormEvent, useEffect, useState} from "react";
import OTPInput from "react-otp-input";
import {useDispatch} from "react-redux";
import styled from "styled-components";
import {
  StyledOtpInput,
  StyledTextButton,
} from "../../../../components/payouts/v2/tabs/payout-settings-tab/otp-view/styles";
import {resendTwoFactorOtpService} from "../../../../redux/auth/login/user-login-service";
import {
  clearTwoFactorChallenge,
  verifyTwoFactorLogin,
} from "../../../../redux/auth/login/user-login-slice";
import {AppDispatch} from "../../../../redux/store";

const StyledTwoFactorOtpInput = styled(StyledOtpInput)`
  && {
    flex: 1 1 0;
    width: 100% !important;
    max-width: 6.8rem;
    min-width: 0;
    height: clamp(5.2rem, 12vw, 6.4rem) !important;
    border-radius: 10px;
    padding: 1.6rem 1rem;
  }
`;

type TTwoFactorLoginView = "totp" | "email" | "recovery";

// Mirrors the backend's 2-minute cooldown between login OTP emails; resending
// any earlier is rejected with a 429
const RESEND_COOLDOWN_SECONDS = 120;

interface TwoFactorLoginModalProps {
  visible: boolean;
  challengeToken: string;
  method: "email" | "totp";
  maskedEmail: string | null;
  cooldownRemainingSeconds: number | null;
}

export function TwoFactorLoginModal({
  visible,
  challengeToken,
  method,
  maskedEmail,
  cooldownRemainingSeconds,
}: TwoFactorLoginModalProps) {
  const dispatch = useDispatch<AppDispatch>();
  const [view, setView] = useState<TTwoFactorLoginView>(method);
  const [otp, setOtp] = useState("");
  const [errorMsg, setErrorMsg] = useState<string | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isResending, setIsResending] = useState(false);
  const [timer, setTimer] = useState(cooldownRemainingSeconds ?? RESEND_COOLDOWN_SECONDS);

  // Reset to the challenge's method view whenever the modal is (re-)opened
  useEffect(() => {
    if (visible) {
      setView(method);
      setOtp("");
      setErrorMsg(null);
      setTimer(cooldownRemainingSeconds ?? RESEND_COOLDOWN_SECONDS);
    }
  }, [visible, method, cooldownRemainingSeconds]);

  // Countdown for the email view's resend link. Keyed on isCountingDown so a
  // resend that resets the timer after it reached 0 starts a fresh interval
  const isCountingDown = timer > 0;
  useEffect(() => {
    if (!visible || view !== "email" || !isCountingDown) {
      return;
    }

    const timerCount = setInterval(() => {
      setTimer((prev) => Math.max(0, prev - 1));
    }, 1000);

    return () => clearInterval(timerCount);
  }, [visible, view, isCountingDown]);

  const handleOtpChange = (value: string) => {
    setOtp(value);
    setErrorMsg(null);
  };

  // Cancels the login challenge entirely — used by the entry views (totp/email)
  const handleCancel = () => {
    dispatch(clearTwoFactorChallenge());
  };

  const handleShowRecovery = () => {
    setView("recovery");
    setOtp("");
    setErrorMsg(null);
  };

  // Recovery is a nested view reached from the totp view, so its Back
  // returns there instead of cancelling the whole challenge
  const handleBackToTotp = () => {
    setView("totp");
    setOtp("");
    setErrorMsg(null);
  };

  const handleResendOtp = async () => {
    setIsResending(true);
    try {
      await resendTwoFactorOtpService({challengeToken});
      setTimer(RESEND_COOLDOWN_SECONDS);
      setOtp("");
      setErrorMsg(null);
    } catch (error: any) {
      setErrorMsg(
        error?.response?.data?.message || error.message || "Failed to resend code"
      );
    } finally {
      setIsResending(false);
    }
  };

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (otp.length < 6) {
      setErrorMsg("Please enter the code to continue");
      return;
    }

    setIsSubmitting(true);
    try {
      await dispatch(
        verifyTwoFactorLogin({
          challengeToken,
          twofaCode: otp,
        })
      ).unwrap();
      // Redirect is handled reactively by LoginForm's own effect once
      // isSuccessUserLogin/profileData update — calling it directly from here
      // would use a stale closure (this component mounted while the post-auth
      // redirect hook was still disabled, since isSuccess flips true only
      // after this dispatch resolves). The modal itself closes on its own via
      // twoFactorChallenge.required, which verifyTwoFactorLogin.fulfilled resets.
    } catch (error: any) {
      setErrorMsg(typeof error === "string" ? error : "Incorrect code. Please try again");
    } finally {
      setIsSubmitting(false);
    }
  };

  const isTotpFullWidth = otp.length < 6 || !!errorMsg;

  const otpInputRow = (
    <OTPInput
      value={otp}
      onChange={handleOtpChange}
      numInputs={6}
      shouldAutoFocus
      inputType="tel"
      containerStyle={{display: "flex", gap: "clamp(8px, 2vw, 16px)", width: "100%"}}
      renderInput={(props) => <StyledTwoFactorOtpInput {...props} />}
    />
  );

  const errorText = errorMsg ? (
    <p style={{color: "#ff4d4f", marginTop: "12px", marginBottom: 0}}>{errorMsg}</p>
  ) : null;

  const splitButtonRow = (onBack: () => void) => (
    <div style={{display: "flex", gap: "16px", width: "100%"}}>
      <Button size="small" style={{flex: 1, height: 46}} onClick={onBack}>
        Back
      </Button>
      <Button
        size="small"
        style={{flex: 1, height: 46}}
        type="primary"
        htmlType="submit"
        disabled={otp.length < 6}
        loading={isSubmitting}
      >
        Verify code
      </Button>
    </div>
  );

  return (
    <Modal
      title="2-factor authentication"
      open={visible}
      onCancel={handleCancel}
      footer={null}
      closable
      centered
      width={520}
      bodyStyle={{padding: 16}}
    >
      {view === "totp" && (
        <form onSubmit={handleSubmit}>
          <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
            <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
              <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                Enter the 6-digit code
              </h2>
              <p style={{margin: 0}}>
                Enter the code from your authenticator app to continue
              </p>
              {otpInputRow}
            </div>
            {errorText}
            {isTotpFullWidth ? (
              <Button
                size="small"
                type="primary"
                htmlType="submit"
                block
                style={{height: 46}}
                disabled={otp.length < 6}
                loading={isSubmitting}
              >
                Verify code
              </Button>
            ) : (
              splitButtonRow(handleCancel)
            )}
            <StyledTextButton
              type="text"
              size="small"
              style={{alignSelf: "flex-start"}}
              onClick={handleShowRecovery}
            >
              <span className="text-underline">
                Can&apos;t access your authenticator app?
              </span>
            </StyledTextButton>
          </div>
        </form>
      )}

      {view === "email" && (
        <form onSubmit={handleSubmit}>
          <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
            <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
              <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                Check your email
              </h2>
              <p style={{margin: 0}}>
                We sent a 6-digit code to {maskedEmail}. Enter it below to continue
              </p>
              {otpInputRow}
            </div>
            {errorText}
            <p style={{margin: 0}}>
              Didn&apos;t get code?{" "}
              {timer > 0 ? (
                <span style={{color: "rgba(0,0,0,0.45)"}}>Resend in {timer}s</span>
              ) : (
                <StyledTextButton
                  type="text"
                  size="small"
                  loading={isResending}
                  onClick={handleResendOtp}
                >
                  <strong style={{fontWeight: 600}}>Resend code</strong>
                </StyledTextButton>
              )}
            </p>
            {splitButtonRow(handleCancel)}
          </div>
        </form>
      )}

      {view === "recovery" && (
        <form onSubmit={handleSubmit}>
          <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
            <div style={{display: "flex", flexDirection: "column", gap: "24px"}}>
              <h2 style={{fontSize: "2rem", fontWeight: 600, margin: 0}}>
                Enter a recovery code
              </h2>
              <p style={{margin: 0}}>
                Enter one of the recovery codes you saved. Each code works once
              </p>
              {otpInputRow}
            </div>
            {errorText}
            {splitButtonRow(handleBackToTotp)}
          </div>
        </form>
      )}
    </Modal>
  );
}
